import React, { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';

import { UseOutletContextConsignment } from './Consignment';
import { StateLanguage } from '../../../../languages/config/StateLanguage';
import { ApiDigitalAdminConsignment } from '../../../../services/admin/consignment.service';
import { Error } from '../../../../models/error.model';
import { Consignment, ConsignmentsData } from '../../../../models/consignment.model';
import { GlobalSwal } from '../../../../styles/swal.style';
import { colorStatusConsignments } from '../../../../scripts/badge.color.script';
import { dateFormat } from '../../../../scripts/date.time.format.script';
import { formatContact, formatTaxRate } from '../../../../scripts/format.value.script';
import { getError } from '../../../../languages/translations/response';
import { sortColumn } from '../../../../scripts/table.script';
import { errorWebHandler } from '../../../../scripts/error.web.handler.script';
import { contactSelected } from '../../../../scripts/list.selected.script';
import { statusConsignmentValue } from '../../../../libraries/status.consignment.library';
import { typeCommissionValue } from '../../../../libraries/type.commission.library';
import { usePaginationUrl } from '../../../../hooks/usePaginationUrl';

import AppSortTable from '../../../../components/element/SortTable';
import AppPagination from '../../../../components/element/Pagination';
import AppPreloader from '../../../../components/Preloader';

export interface ConsignmentListPageProps {};

let errorResponse: Error, consignmentsResponse: ConsignmentsData;

const ConsignmentListPage: React.FunctionComponent<ConsignmentListPageProps> = props => {
  const {setRoute, companyForUser, contactsForCompany} = UseOutletContextConsignment()
  const {lang} = StateLanguage()
  const { page: urlPage, setPage: setUrlPage } = usePaginationUrl()

  const [mounted, setMounted] = useState(false)
  const [filter, setFilter] = useState({contact: '', status: '', sort_field: '', sort_mode: ''})
  const [pagination, setPagination] = useState({current: 1, limit: 20, total: 0, pages: [1] as number[]})
  const [consignments, setConsignments] = useState<Consignment[] | null>(null)

  const loadConsignments = async (id_company: string, page: number) => {
    await ApiDigitalAdminConsignment.getConsignments(id_company, filter.contact, filter.status, filter.sort_field, filter.sort_mode, page, pagination.limit).then( (response) => {
      if (response.status === 200) {
        consignmentsResponse = response.data

        localStorage.setItem('filter_consignments', JSON.stringify({
          company_id: id_company,
          contact: filter.contact,
          status: filter.status,
          sort_field: filter.sort_field,
          sort_mode: filter.sort_mode
        }))

        const totalPages = consignmentsResponse.pagination?.pages || 1

        if (page > totalPages && totalPages >= 1) {
          setUrlPage(totalPages)
          loadConsignments(id_company, totalPages)
          return
        }

        let pagesOfPagination: number[] = []
        for (let i = 1; i <= totalPages; i++) {
          pagesOfPagination.push(i)
        }

        setConsignments(consignmentsResponse.items)
        setPagination({...pagination, current: consignmentsResponse.pagination?.page || 1, total: consignmentsResponse.pagination?.total || 0, pages: pagesOfPagination})
        setUrlPage(consignmentsResponse.pagination?.page || 1)
      } else {
        errorResponse = response.data

        GlobalSwal.fire({
          title: getError(errorResponse.code, lang.code, errorResponse.message),
          text: lang.labels.sorryLooksLikeThereAreSomeErrorstryAgain,
          icon: 'error',
          confirmButtonText: lang.labels.okGotIt
        })
      }
    }).catch( (error) => {
      errorWebHandler(lang, error)
    })
  }

  const loadPagination = (current: number) => {
    if (!companyForUser) return null
    setUrlPage(current)
    loadConsignments(companyForUser.id, current)
  }

  const executeSortList = (item: string) => {
    if (!companyForUser) return null

    sortColumn(item, filter)
    setUrlPage(1)
    loadConsignments(companyForUser.id, 1)
  }

  useEffect( () => {
    let filter_consignments: any = (localStorage.getItem('filter_consignments')) ? JSON.parse(localStorage.getItem('filter_consignments')!) : null

    if (companyForUser) {
      filter.contact = (filter_consignments && filter_consignments.company_id === companyForUser.id) ? filter_consignments.contact : ''
      filter.status = (filter_consignments && filter_consignments.company_id === companyForUser.id) ? filter_consignments.status : ''
      filter.sort_field = (filter_consignments && filter_consignments.company_id === companyForUser.id) ? filter_consignments.sort_field : ''
      filter.sort_mode = (filter_consignments && filter_consignments.company_id === companyForUser.id) ? filter_consignments.sort_mode : ''
      pagination.total = 0

      setConsignments(null)
      loadConsignments(companyForUser.id, urlPage)
    }

  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyForUser])

  useEffect( () => {
    setMounted(true)
    setRoute({path: {root: lang.labels.consignments, branch: lang.labels.listConsignments}, company: true})

    return () => setMounted(false)

  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  if (!mounted) return null

  return (
    <div className="w-100 h-100">
      { companyForUser && contactsForCompany && consignments
        ?
        <div className="card card-flush">
          <div className="card-header border-0 pt-5">
            <div className="card-title">{lang.labels.listConsignments}</div>
            <div className="card-toolbar">
              <Link to="/main/purchase/consignment/create" className="btn btn-primary mb-1 me-0">
                <div className="d-flex align-items-center">
                  <span className="svg-icon svg-icon-2 ms-0">
                    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                      <rect opacity="0.3" x="2" y="2" width="20" height="20" rx="5" fill="black" />
                      <rect x="10.8891" y="17.8033" width="12" height="2" rx="1" transform="rotate(-90 10.8891 17.8033)" fill="black" />
                      <rect x="6.01041" y="10.9247" width="12" height="2" rx="1" fill="black" />
                    </svg>
                  </span>
                  {lang.labels.newConsignment}
                </div>
              </Link>
            </div>
          </div>
          <div className="card-body pt-0">
            <div className="dataTables_wrapper">
              <div className="table-responsive">
                <table className="table align-middle table-row-dashed mb-2">
                  <thead className="border-bottom">
                    <tr className="text-start text-muted text-uppercase fw-bolder fs-7">
                      <th className="w-125px">
                        <div className="d-flex align-items-center text-nowrap cursor-pointer" onClick={() => executeSortList("created_at")}>
                          <span className="me-1">{lang.labels.date}</span>
                          <AppSortTable attribute={"created_at"} field={filter.sort_field} mode={filter.sort_mode}></AppSortTable>
                        </div>
                      </th>
                      <th className="min-w-100px">
                        <div className="d-flex align-items-center text-nowrap cursor-pointer" onClick={() => executeSortList("id_contact")}>
                          <span className="me-1">{lang.labels.provider}</span>
                          <AppSortTable attribute={"id_contact"} field={filter.sort_field} mode={filter.sort_mode}></AppSortTable>
                        </div>
                      </th>
                      <th className="min-w-100px">
                        <div className="d-flex align-items-center text-nowrap cursor-pointer" onClick={() => executeSortList("status")}>
                          <span className="me-1">{lang.labels.status}</span>
                          <AppSortTable attribute={"status"} field={filter.sort_field} mode={filter.sort_mode}></AppSortTable>
                        </div>
                      </th>
                      <th className="min-w-100px">
                        <div className="d-flex align-items-center text-nowrap cursor-pointer" onClick={() => executeSortList("start_date")}>
                          <span className="me-1">{lang.labels.startDate}</span>
                          <AppSortTable attribute={"start_date"} field={filter.sort_field} mode={filter.sort_mode}></AppSortTable>
                        </div>
                      </th>
                      <th className="min-w-100px">
                        <div className="d-flex align-items-center text-nowrap cursor-pointer" onClick={() => executeSortList("end_date")}>
                          <span className="me-1">{lang.labels.endDate}</span>
                          <AppSortTable attribute={"end_date"} field={filter.sort_field} mode={filter.sort_mode}></AppSortTable>
                        </div>
                      </th>
                      <th className="min-w-100px">
                        <div className="d-flex align-items-center text-nowrap cursor-pointer" onClick={() => executeSortList("commission_type")}>
                          <span className="me-1">{lang.labels.commissionType}</span>
                          <AppSortTable attribute={"commission_type"} field={filter.sort_field} mode={filter.sort_mode}></AppSortTable>
                        </div>
                      </th>
                      <th className="min-w-100px text-end">
                        <div className="d-flex align-items-center justify-content-end text-nowrap cursor-pointer" onClick={() => executeSortList("commission_rate")}>
                          <span className="me-1">{lang.labels.commissionRate}</span>
                          <AppSortTable attribute={"commission_rate"} field={filter.sort_field} mode={filter.sort_mode}></AppSortTable>
                        </div>
                      </th>
                    </tr>
                  </thead>
                  { pagination.total > 0
                    ?
                    <tbody className="text-secondary fw-bold">
                      { consignments.map (( (item, index) => { return (
                        <tr key={index} className="row-dashed">
                          <td>
                            <Link to={"/main/purchase/consignment/view/" + item.id} className="fw-bolder text-nowrap text-dark text-hover-primary">{dateFormat(lang.code, item.created_at)}</Link>
                          </td>
                          <td>{(item as any).provider ? formatContact((item as any).provider) : '-'}</td>
                          <td>
                            <span className={`badge badge-light-${colorStatusConsignments(item.status)} fs-7`}>{statusConsignmentValue(lang, item.status)}</span>
                          </td>
                          <td className="fs-7">{dateFormat(lang.code, item.start_date)}</td>
                          <td className="fs-7">{dateFormat(lang.code, item.end_date)}</td>
                          <td>{typeCommissionValue(lang, item.commission_type)}</td>
                          <td className="text-end text-nowrap">{formatTaxRate(item.commission_rate)}</td>
                        </tr>
                      )}))}
                    </tbody>
                    :
                    <tbody>
                      <tr className="text-secondary fw-bold">
                        <td colSpan={7} className="text-center">{lang.labels.noRecordsFound}</td>
                      </tr>
                    </tbody>
                  }
                </table>
              </div>
              <AppPagination showOption={false} pagination={pagination} loadPagination={loadPagination}></AppPagination>
            </div>
          </div>
        </div>
        :
        <AppPreloader></AppPreloader>
      }
    </div>
  )
}

export default ConsignmentListPage;
